import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Archive, ArchiveRestore, Copy, FileDown, Leaf, Loader2, LogOut, MapPin, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import {
  adminArchiveProduct,
  adminCloneProduct,
  adminCreateProduct,
  adminDeleteProduct,
  adminDownloadInvoice,
  adminDownloadPriceList,
  adminGetEnquiries,
  adminGetOrders,
  adminGetProducts,
  adminGetStats,
  adminLogin,
  adminUpdateOrderStatus,
  adminUpdateProduct,
  formatApiError,
  inr,
} from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { ProductFormDialog } from "@/components/admin/ProductFormDialog";
import { CouponsTab } from "@/components/admin/CouponsTab";
import { ReviewsTab } from "@/components/admin/ReviewsTab";
import { CategoriesTab } from "@/components/admin/CategoriesTab";
import { SettingsTab } from "@/components/admin/SettingsTab";
import { PriceListTab } from "@/components/admin/PriceListTab";

const STATUSES = ["placed", "payment_pending", "paid", "confirmed", "shipped", "delivered", "cancelled"];

const STATUS_STYLES = {
  placed: "bg-secondary text-secondary-foreground",
  payment_pending: "bg-amber-100 text-amber-800",
  paid: "bg-emerald-100 text-emerald-800",
  confirmed: "bg-sky-100 text-sky-800",
  shipped: "bg-indigo-100 text-indigo-800",
  delivered: "bg-primary/15 text-primary",
  cancelled: "bg-red-100 text-red-700",
};

const fmtDate = (iso) => new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem("classina_admin") || null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [savingProduct, setSavingProduct] = useState(false);
  const [mapOpen, setMapOpen] = useState({});

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const [o, e, s, p] = await Promise.all([adminGetOrders(t), adminGetEnquiries(t), adminGetStats(t), adminGetProducts(t)]);
      setOrders(o);
      setEnquiries(e);
      setStats(s);
      setProducts(p);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("classina_admin");
        setToken(null);
        toast.error("Session expired — please log in again");
      } else {
        toast.error("Could not load dashboard data");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setError("");
    try {
      const data = await adminLogin(email, password);
      localStorage.setItem("classina_admin", data.token);
      setToken(data.token);
      toast.success("Welcome back");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("classina_admin");
    setToken(null);
  };

  const updateStatus = async (orderId, status) => {
    try {
      await adminUpdateOrderStatus(token, orderId, status);
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status } : o)));
      toast.success(`Order marked ${status}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  const saveProduct = async (payload) => {
    setSavingProduct(true);
    try {
      if (editingProduct) {
        await adminUpdateProduct(token, editingProduct.slug, payload);
        toast.success(`${payload.name} updated`);
      } else {
        await adminCreateProduct(token, payload);
        toast.success(`${payload.name} added to the store`);
      }
      setFormOpen(false);
      setEditingProduct(null);
      setProducts(await adminGetProducts(token));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingProduct(false);
    }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Delete "${p.name}" from the store? This cannot be undone.`)) return;
    try {
      await adminDeleteProduct(token, p.slug);
      setProducts((prev) => prev.filter((x) => x.slug !== p.slug));
      toast.success(`${p.name} deleted`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const cloneProduct = async (p) => {
    try {
      const created = await adminCloneProduct(token, p.slug);
      setProducts(await adminGetProducts(token));
      toast.success(`"${created.name}" created — edit it to make it your own`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleArchive = async (p) => {
    if (!p.archived && !window.confirm(`Archive "${p.name}"? It will be hidden from the store until you unarchive it.`)) return;
    try {
      await adminArchiveProduct(token, p.slug, !p.archived);
      setProducts((prev) => prev.map((x) => (x.slug === p.slug ? { ...x, archived: !p.archived } : x)));
      toast.success(p.archived ? `"${p.name}" is back on the store` : `"${p.name}" archived — hidden from the store`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm bg-white/70 backdrop-blur-sm border border-border/60 rounded-2xl p-8 shadow-sm"
        >
          <div className="mb-8">
            <img src="/classina-logo.png" alt="Classina Products" className="h-12 w-auto" />
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mt-3">Store Admin</p>
          </div>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Email</span>
            <input
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 w-full rounded-md border border-input bg-white/60 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary transition"
            />
          </label>
          <label className="block mt-4">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">Password</span>
            <input
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 w-full rounded-md border border-input bg-white/60 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary transition"
            />
          </label>
          {error && <p data-testid="admin-login-error" className="text-sm text-destructive mt-3">{error}</p>}
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={loggingIn}
            className="w-full mt-6 rounded-full bg-primary text-primary-foreground py-3 text-sm font-bold flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-60"
          >
            {loggingIn && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
          </button>
          <Link to="/" data-testid="admin-back-link" className="block text-center text-xs text-muted-foreground mt-5 hover:text-foreground transition-colors">
            ← Back to store
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-[#F5F4F0]/85 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/classina-logo.png" alt="Classina Products" className="h-8 w-auto" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-refresh-button"
              onClick={() => load(token)}
              className="p-2.5 rounded-full hover:bg-secondary transition-colors"
              aria-label="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              data-testid="admin-price-list-button"
              onClick={() => adminDownloadPriceList(token).catch(() => toast.error("Could not download price list"))}
              className="flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-xs font-bold hover:bg-secondary transition-colors"
            >
              <FileDown className="w-3.5 h-3.5" /> Price list
            </button>
            <Link to="/" data-testid="admin-store-link" className="text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors px-3">
              View store
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-xs font-bold hover:bg-secondary transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {[
              { label: "Revenue", value: inr(stats.revenue), testid: "stat-revenue" },
              { label: "Orders", value: stats.orders, testid: "stat-orders" },
              { label: "Enquiries", value: stats.enquiries, testid: "stat-enquiries" },
              { label: "To fulfil", value: stats.pending, testid: "stat-pending" },
            ].map((s) => (
              <div key={s.label} data-testid={s.testid} className="bg-white/70 border border-border/60 rounded-xl p-5">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">{s.label}</p>
                <p className="font-serif text-2xl md:text-3xl font-semibold mt-1">{s.value}</p>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 mb-6">
          {["orders", "enquiries", "products", "categories", "coupons", "reviews", "price list", "settings"].map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`rounded-full px-6 py-2.5 text-xs font-bold uppercase tracking-[0.1em] transition-all ${
                tab === t ? "bg-primary text-primary-foreground shadow-md" : "bg-secondary/60 text-muted-foreground hover:bg-secondary"
              }`}
            >
              {t} {t === "orders" ? `(${orders.length})` : t === "enquiries" ? `(${enquiries.length})` : t === "products" ? `(${products.length})` : ""}
            </button>
          ))}
          {tab === "products" && (
            <button
              data-testid="add-product-button"
              onClick={() => { setEditingProduct(null); setFormOpen(true); }}
              className="ml-auto rounded-full bg-accent text-accent-foreground px-5 py-2.5 text-xs font-bold uppercase tracking-[0.1em] flex items-center gap-1.5 hover:-translate-y-0.5 transition-transform"
            >
              <Plus className="w-3.5 h-3.5" /> Add product
            </button>
          )}
        </div>

        {tab === "orders" && (
          <div className="space-y-4" data-testid="admin-orders-list">
            {orders.length === 0 && <p className="text-muted-foreground text-sm py-10 text-center">No orders yet.</p>}
            {orders.map((o) => (
              <div key={o.id} data-testid={`admin-order-${o.order_no}`} className="bg-white/70 border border-border/60 rounded-xl p-5 md:p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-serif text-lg font-semibold">{o.order_no}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{fmtDate(o.created_at)}</p>
                    <p className="text-sm mt-2 font-medium">{o.customer.name} · {o.customer.phone}</p>
                    <p className="text-xs text-muted-foreground">{o.customer.address}, {o.customer.city}, {o.customer.state} — {o.customer.pincode}</p>
                    {o.customer.gstin && (
                      <p className="text-xs text-primary font-semibold mt-1">GST invoice · {o.customer.business_name} · {o.customer.gstin}</p>
                    )}
                    {o.customer.location?.maps_url && (
                      <div className="mt-1.5 flex items-center gap-4">
                        <button
                          type="button"
                          data-testid={`order-map-toggle-${o.order_no}`}
                          onClick={() => setMapOpen((prev) => ({ ...prev, [o.id]: !prev[o.id] }))}
                          className="text-xs text-primary font-semibold inline-flex items-center gap-1 hover:underline"
                        >
                          <MapPin className="w-3 h-3" /> {mapOpen[o.id] ? "Hide delivery map" : "Show delivery map"}
                        </button>
                        <a
                          href={o.customer.location.maps_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`order-location-${o.order_no}`}
                          className="text-xs text-muted-foreground font-semibold inline-flex items-center gap-1 hover:underline"
                        >
                          Open in Google Maps ↗
                        </a>
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-serif text-xl font-semibold">{inr(o.total)}</p>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mt-0.5">{o.payment_mode === "online" ? "Online payment" : "COD"}</p>
                    <select
                      data-testid={`order-status-${o.order_no}`}
                      value={o.status}
                      onChange={(e) => updateStatus(o.id, e.target.value)}
                      className={`mt-2 text-xs font-bold rounded-full px-3 py-1.5 border-0 outline-none cursor-pointer ${STATUS_STYLES[o.status] || "bg-secondary"}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                  </div>
                </div>
                {o.customer.location?.lat != null && mapOpen[o.id] && (
                  <iframe
                    title={`Delivery map for ${o.order_no}`}
                    data-testid={`order-map-${o.order_no}`}
                    src={`https://maps.google.com/maps?q=${o.customer.location.lat},${o.customer.location.lng}&z=16&output=embed`}
                    className="w-full h-60 rounded-xl border border-border/60 mt-4"
                    loading="lazy"
                  />
                )}
                <div className="mt-4 pt-4 border-t border-border/60 flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
                  {o.items.map((i, idx) => (
                    <span key={idx}>{i.name}{i.variant ? ` (${i.variant})` : ""}{i.pack ? ` [${i.pack}]` : ""} × {i.qty}</span>
                  ))}
                  <button
                    data-testid={`admin-invoice-${o.order_no}`}
                    onClick={() => adminDownloadInvoice(token, o.id, o.order_no)}
                    className="text-primary font-bold text-xs hover:underline"
                  >
                    ↓ Invoice PDF
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "enquiries" && (
          <div className="space-y-4" data-testid="admin-enquiries-list">
            {enquiries.length === 0 && <p className="text-muted-foreground text-sm py-10 text-center">No enquiries yet.</p>}
            {enquiries.map((e) => (
              <div key={e.id} data-testid={`admin-enquiry-${e.id}`} className="bg-white/70 border border-border/60 rounded-xl p-5 md:p-6">
                <div className="flex flex-wrap justify-between gap-2">
                  <p className="font-serif text-lg font-semibold">{e.name}</p>
                  <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-primary bg-primary/10 rounded-full px-3 py-1 h-fit">{e.subject}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{e.email}{e.phone ? ` · ${e.phone}` : ""} · {fmtDate(e.created_at)}</p>
                <p className="text-sm mt-3 leading-relaxed">{e.message}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "products" && (
          <div className="space-y-3" data-testid="admin-products-list">
            {products.map((p) => (
              <div
                key={p.slug}
                data-testid={`admin-product-${p.slug}`}
                className={`bg-white/70 border rounded-xl p-4 flex items-center gap-4 ${p.archived ? "border-dashed border-border opacity-60" : "border-border/60"}`}
              >
                <img src={p.image} alt={p.name} className="w-14 h-14 rounded-lg object-cover border border-border/60 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-serif font-semibold leading-snug truncate">
                    {p.name}
                    {p.archived && (
                      <span data-testid={`archived-badge-${p.slug}`} className="ml-2 align-middle text-[9px] uppercase tracking-[0.15em] font-bold bg-secondary text-muted-foreground rounded-full px-2.5 py-0.5">
                        Archived
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {p.category} · {inr(p.price)}
                    <span className={`ml-1.5 ${(p.stock ?? 100) <= 0 ? "text-destructive font-bold" : ""}`} data-testid={`stock-${p.slug}`}>
                      · {(p.stock ?? 100) <= 0 ? "Out of stock" : `Stock: ${p.stock ?? 100}`}
                    </span>
                    {p.mrp > p.price && <span className="line-through ml-1.5">{inr(p.mrp)}</span>}
                    {(p.images?.length || 0) > 0 && <span className="ml-1.5">· {p.images.length + 1} photos</span>}
                    {p.video && <span className="ml-1.5">· video</span>}
                    {p.variants?.length > 0 && <span className="ml-1.5 text-primary font-semibold">· {p.variants.length} colours</span>}
                    {p.badge && <span className="ml-1.5 text-accent font-semibold">· {p.badge}</span>}
                  </p>
                </div>
                <button
                  data-testid={`edit-product-${p.slug}`}
                  onClick={() => { setEditingProduct(p); setFormOpen(true); }}
                  className="p-2.5 rounded-full border border-border hover:bg-secondary transition-colors"
                  aria-label={`Edit ${p.name}`}
                  title="Edit product"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  data-testid={`clone-product-${p.slug}`}
                  onClick={() => cloneProduct(p)}
                  className="p-2.5 rounded-full border border-border hover:bg-secondary transition-colors"
                  aria-label={`Clone ${p.name}`}
                  title="Clone product"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  data-testid={`archive-product-${p.slug}`}
                  onClick={() => toggleArchive(p)}
                  className={`p-2.5 rounded-full border transition-colors ${
                    p.archived
                      ? "border-primary/40 text-primary hover:bg-primary/10"
                      : "border-border text-muted-foreground hover:text-amber-700 hover:border-amber-500/40"
                  }`}
                  aria-label={p.archived ? `Unarchive ${p.name}` : `Archive ${p.name}`}
                  title={p.archived ? "Unarchive — show in store" : "Archive — hide from store"}
                >
                  {p.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                </button>
                <button
                  data-testid={`delete-product-${p.slug}`}
                  onClick={() => deleteProduct(p)}
                  className="p-2.5 rounded-full border border-border text-muted-foreground hover:text-destructive hover:border-destructive/40 transition-colors"
                  aria-label={`Delete ${p.name}`}
                  title="Delete product"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === "coupons" && <CouponsTab token={token} />}

        {tab === "reviews" && <ReviewsTab token={token} />}

        {tab === "categories" && <CategoriesTab token={token} />}

        {tab === "settings" && <SettingsTab token={token} />}

        {tab === "price list" && <PriceListTab token={token} />}
      </div>

      <ProductFormDialog
        open={formOpen}
        onOpenChange={(o) => { setFormOpen(o); if (!o) setEditingProduct(null); }}
        product={editingProduct}
        categories={[...new Set(products.map((p) => p.category))]}
        onSave={saveProduct}
        saving={savingProduct}
      />
    </div>
  );
}
